/**
 * Central de Operações Keeta — Painel Operacional
 * 6 abas: Visão Geral · Fila de Eventos · Motoristas · Linha do Tempo · Segurança · Métricas
 * Atualização automática inteligente (pausa quando a aba está invisível).
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MainLayout } from "@/components/Layout";

// Centralized axios instance from /lib/api.js — sends JWT via interceptor and
// handles 401 → /login automatically. NEVER use raw axios here.
const OPS_PREFIX = "/keeta/operations";
const url = (path) => `${OPS_PREFIX}${path}`;

// ─── Hook: polling com pause em aba invisível ───────────────────────
// immediate=true (default) → invoca fn imediatamente; passe false quando
// outro useEffect já dispara o load inicial (ex: filtros).
const useSmartPolling = (fn, intervalMs, immediate = true) => {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    let timer;
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      if (!document.hidden) {
        try { await fnRef.current(); } catch (e) { /* swallow */ }
      }
      if (alive) timer = setTimeout(tick, intervalMs);
    };
    if (immediate) {
      tick();
    } else {
      timer = setTimeout(tick, intervalMs);
    }
    const onVis = () => { if (!document.hidden && alive) fnRef.current(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearTimeout(timer); document.removeEventListener("visibilitychange", onVis); };
  }, [intervalMs, immediate]);
};

const Badge = ({ status, children }) => {
  const map = {
    PENDING: "bg-amber-100 text-amber-800 border-amber-200",
    IN_PROGRESS: "bg-sky-100 text-sky-800 border-sky-200",
    SUCCESS: "bg-emerald-100 text-emerald-800 border-emerald-200",
    DEAD: "bg-rose-100 text-rose-800 border-rose-200",
    CRITICAL: "bg-rose-100 text-rose-800 border-rose-200",
    WARNING: "bg-amber-100 text-amber-800 border-amber-200",
    INFO: "bg-sky-100 text-sky-800 border-sky-200",
    healthy: "bg-emerald-100 text-emerald-800 border-emerald-200",
    degraded: "bg-rose-100 text-rose-800 border-rose-200",
    online: "bg-emerald-100 text-emerald-800",
    offline: "bg-rose-100 text-rose-800",
  };
  const cls = map[status] || "bg-zinc-100 text-zinc-700 border-zinc-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-mono font-semibold ${cls}`}
          data-testid={`badge-${status}`}>
      {children || status}
    </span>
  );
};

const StatCard = ({ title, value, subtitle, color = "text-zinc-900", testId, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4 hover:shadow-md transition" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">{title}</div>
    <div className={`text-3xl font-bold mt-2 ${color}`}>{value}</div>
    {subtitle && <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>}
    {children}
  </div>
);

const Skeleton = () => (
  <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4 animate-pulse">
    <div className="h-3 bg-zinc-200 rounded w-1/3 mb-3"></div>
    <div className="h-8 bg-zinc-200 rounded w-1/2"></div>
  </div>
);

// Badge do modo operacional (DESLIGADO / DRY-RUN / AO VIVO)
const ModeBadge = ({ mode }) => {
  const map = {
    disabled: { cls: "bg-zinc-200 text-zinc-700 border-zinc-300", label: "DESLIGADO", title: "Workers Keeta desativados" },
    dry_run:  { cls: "bg-amber-100 text-amber-900 border-amber-300", label: "MODO SIMULADO", title: "Workers ON · respostas simuladas (HTTP 202) · não envia para Keeta" },
    live:     { cls: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "AO VIVO", title: "Integração real com Keeta" },
  };
  const m = map[mode] || { cls: "bg-zinc-100 text-zinc-700 border-zinc-200", label: mode || "?", title: "" };
  return (
    <span title={m.title}
          data-testid={`mode-badge-${mode}`}
          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-mono font-bold uppercase tracking-wider ${m.cls}`}>
      {m.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────
//  TAB: OVERVIEW
// ─────────────────────────────────────────────────────────────────────
const OverviewTab = () => {
  const [health, setHealth] = useState(null);
  const [alerts, setAlerts] = useState({ alerts: [], count: 0, severities: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [h, a] = await Promise.all([api.get(url("/health")), api.get(url("/alerts"))]);
      setHealth(h.data); setAlerts(a.data); setLoading(false);
    } catch (e) { setLoading(false); }
  }, []);
  useSmartPolling(load, 15000);

  if (loading || !health) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <Skeleton key={i} />)}</div>;
  }

  return (
    <div className="space-y-4" data-testid="overview-tab">
      {/* Status banner */}
      <div className={`rounded-xl border p-4 ${health.status === "healthy" ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <Badge status={health.status} >{health.status === "healthy" ? "saudável" : "degradado"}</Badge>
          {health.mode && <ModeBadge mode={health.mode} />}
          <span className="text-sm font-semibold">
            Banco: {health.db_ok ? "OK" : "INDISPONÍVEL"}
          </span>
          {health.worker_last_success_seconds_ago !== null && (
            <span className="text-xs text-zinc-600">
              Última execução do worker: {Math.floor(health.worker_last_success_seconds_ago)}s atrás
            </span>
          )}
          {alerts.count > 0 && <Badge status="WARNING">{alerts.count} alertas</Badge>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="Fila Pendente" value={health.outbox.pending}
                  color={health.outbox.pending > 50 ? "text-amber-600" : "text-zinc-900"} testId="card-pending" />
        <StatCard title="Eventos Mortos" value={health.outbox.dead}
                  color={health.outbox.dead > 0 ? "text-rose-600" : "text-zinc-900"} testId="card-dead" />
        <StatCard title="Sucessos (5min)" value={health.outbox.success_5min}
                  color="text-emerald-600" testId="card-success-5m" />
        <StatCard title="Motoristas GPS Inativo" value={health.tracking_stale_drivers}
                  color={health.tracking_stale_drivers > 0 ? "text-amber-600" : "text-zinc-900"} testId="card-stale" />
        <StatCard title="Em Andamento" value={health.outbox.in_progress} testId="card-in-progress" />
        <StatCard title="Reprocessando" value={health.outbox.retrying} testId="card-retrying" />
        <StatCard title="Alertas Críticos" value={alerts.severities?.CRITICAL ?? 0}
                  color={(alerts.severities?.CRITICAL ?? 0) > 0 ? "text-rose-600" : "text-zinc-900"} testId="card-critical" />
        <StatCard title="Alertas de Atenção" value={alerts.severities?.WARNING ?? 0}
                  color={(alerts.severities?.WARNING ?? 0) > 0 ? "text-amber-600" : "text-zinc-900"} testId="card-warning" />
      </div>

      {/* Painel de alertas */}
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">🚨 Alertas Operacionais
            <span className="text-sm font-normal text-zinc-500">({alerts.count})</span>
          </h3>
        </div>
        <div className="divide-y divide-zinc-100">
          {alerts.alerts.length === 0 ? (
            <div className="p-6 text-center text-emerald-700 font-medium">✅ Nenhum alerta — Sistema operando normalmente</div>
          ) : alerts.alerts.map((a, i) => (
            <div key={i} className="p-3 flex items-center gap-3" data-testid={`alert-${a.code}`}>
              <Badge status={a.severity} >{a.severity === "CRITICAL" ? "CRÍTICO" : a.severity === "WARNING" ? "ATENÇÃO" : a.severity}</Badge>
              <div className="flex-1">
                <div className="font-mono text-[10px] text-zinc-500 uppercase">{a.code}</div>
                <div className="text-sm">{a.message}</div>
              </div>
              {a.order_id && <span className="text-[11px] font-mono text-zinc-500">pedido={a.order_id?.slice(0,12)}…</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
//  TAB: OUTBOX
// ─────────────────────────────────────────────────────────────────────
const OutboxTab = () => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [orderFilter, setOrderFilter] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [viewItem, setViewItem] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (orderFilter)  params.order_id = orderFilter;
    if (eventFilter)  params.event_type = eventFilter;
    const r = await api.get(url("/outbox"), { params });
    setItems(r.data.items || []);
  }, [statusFilter, orderFilter, eventFilter]);

  useSmartPolling(load, 15000, /* immediate */ false);
  useEffect(() => { load(); }, [load]);

  const [retryingId, setRetryingId] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);

  const retry = async (id) => {
    if (!window.confirm(`Reagendar evento #${id} para reprocessamento imediato?`)) return;
    setRetryingId(id);
    try {
      await api.post(url(`/outbox/${id}/retry`));
      toast.success(`Evento #${id} reagendado para reprocessamento`);
      load();
    } catch (e) {
      toast.error(`Falha ao reagendar #${id}: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRetryingId(null);
    }
  };
  const retryAllDead = async () => {
    if (!window.confirm("⚠️ Reagendar TODOS os eventos MORTOS?")) return;
    setRetryingAll(true);
    try {
      const r = await api.post(url("/outbox/retry-all-dead"));
      toast.success(`${r.data.requeued} eventos reagendados`);
      load();
    } catch (e) {
      toast.error(`Falha no reprocessamento em lote: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRetryingAll(false);
    }
  };

  const events = useMemo(() => ["PICKUP_ONGOING","ARRIVED_AT_MERCHANT","ORDER_PICKED","DELIVERY_ONGOING","ARRIVED_AT_CUSTOMER","ORDER_DELIVERED","CANCELLED"], []);

  return (
    <div className="space-y-3" data-testid="outbox-tab">
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-3 flex flex-wrap gap-2 items-center">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-zinc-300 rounded-lg px-3 py-1.5 text-sm" data-testid="filter-status">
          <option value="">Todos os status</option>
          <option value="PENDING">PENDENTE</option>
          <option value="IN_PROGRESS">EM ANDAMENTO</option>
          <option value="SUCCESS">SUCESSO</option>
          <option value="DEAD">MORTO</option>
        </select>
        <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
                className="border border-zinc-300 rounded-lg px-3 py-1.5 text-sm" data-testid="filter-event">
          <option value="">Todos os eventos</option>
          {events.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <input value={orderFilter} onChange={(e) => setOrderFilter(e.target.value)}
               placeholder="ID do pedido Keeta" className="border border-zinc-300 rounded-lg px-3 py-1.5 text-sm font-mono"
               data-testid="filter-order" />
        <button onClick={load} className="bg-zinc-900 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-zinc-700"
                data-testid="btn-search">Buscar</button>
        <div className="flex-1"></div>
        <button onClick={retryAllDead} disabled={retryingAll}
                className="bg-rose-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-rose-700 disabled:opacity-50"
                data-testid="btn-retry-all-dead">{retryingAll ? "Reagendando..." : "Reprocessar todos MORTOS"}</button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-600">
            <tr>
              <th className="p-3">ID</th><th>Pedido</th><th>Evento</th><th>Status</th>
              <th>Tent.</th><th>Último Erro</th><th>Atualizado</th><th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {items.map((it) => (
              <tr key={it.id} className="hover:bg-zinc-50" data-testid={`outbox-row-${it.id}`}>
                <td className="p-3 font-mono text-xs">{it.id}</td>
                <td className="font-mono text-xs">{it.keeta_order_id?.slice(0, 16)}…</td>
                <td className="font-mono text-xs">{it.event_type}</td>
                <td><Badge status={it.status}>{({PENDING:"PENDENTE",IN_PROGRESS:"EM ANDAMENTO",SUCCESS:"SUCESSO",DEAD:"MORTO",FAILED:"FALHOU"}[it.status]) || it.status}</Badge>
                  {it.simulated && <span className="ml-1 text-[10px] font-mono px-1 rounded bg-amber-100 text-amber-800 border border-amber-200" title="Simulado em DRY_RUN — não enviado à Keeta">SIM</span>}
                </td>
                <td className="font-semibold">{it.attempts}</td>
                <td className="text-xs text-rose-600 max-w-[250px] truncate" title={it.last_error}>{it.last_error}</td>
                <td className="text-[11px] text-zinc-500 font-mono">{it.updated_at?.replace("T", " ").slice(0, 19)}</td>
                <td className="space-x-1">
                  <button onClick={() => setViewItem(it)} className="text-sky-600 hover:underline text-xs" data-testid={`btn-view-${it.id}`}>Detalhes</button>
                  {(it.status === "DEAD" || it.status === "FAILED") && (
                    <button onClick={() => retry(it.id)} disabled={retryingId === it.id} className="text-emerald-600 hover:underline text-xs disabled:opacity-50" data-testid={`btn-retry-${it.id}`}>{retryingId === it.id ? "..." : "Reprocessar"}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-6 text-center text-zinc-500">Nenhum evento encontrado</div>}
      </div>

      {viewItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setViewItem(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()} data-testid="modal-payload">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold">Evento #{viewItem.id} — {viewItem.event_type}</h3>
              <button onClick={() => setViewItem(null)} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none">×</button>
            </div>
            <div className="space-y-3">
              <div><Badge status={viewItem.status} /> · tentativas: {viewItem.attempts}</div>
              {viewItem.last_error && (
                <div><div className="text-xs text-zinc-500 mb-1">Último Erro</div>
                  <pre className="bg-rose-50 text-rose-900 rounded p-2 text-xs overflow-x-auto">{viewItem.last_error}</pre></div>
              )}
              {viewItem.last_response && (
                <div><div className="text-xs text-zinc-500 mb-1">Última Resposta</div>
                  <pre className="bg-zinc-50 rounded p-2 text-xs overflow-x-auto">{JSON.stringify(viewItem.last_response, null, 2)}</pre></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
//  TAB: DRIVERS
// ─────────────────────────────────────────────────────────────────────
const DriversTab = () => {
  const [data, setData] = useState({ drivers: [], count: 0 });
  const load = useCallback(async () => {
    const r = await api.get(url("/drivers/live")); setData(r.data);
  }, []);
  useSmartPolling(load, 10000);

  return (
    <div className="space-y-3" data-testid="drivers-tab">
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
        <h3 className="font-semibold flex items-center gap-2">🛵 Motoristas em Entrega ({data.count})</h3>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-600">
            <tr><th className="p-3">Motorista</th><th>Telefone</th><th>Placa</th><th>Pedido Keeta</th><th>GPS</th><th>Idade</th><th>Status</th></tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {data.drivers.map((d) => (
              <tr key={d.driver_id} className="hover:bg-zinc-50" data-testid={`driver-row-${d.driver_id}`}>
                <td className="p-3 font-medium">{d.name}</td>
                <td className="text-xs">{d.phone}</td>
                <td className="font-mono text-xs">{d.plate}</td>
                <td className="font-mono text-xs">{d.keeta_order_id?.slice(0,16)}…</td>
                <td className="text-xs font-mono">{(d.lat != null && d.lng != null) ? `${d.lat.toFixed(5)}, ${d.lng.toFixed(5)}` : <span className="text-rose-500">SEM GPS</span>}</td>
                <td>{d.gps_age_seconds === null ? "—" : (
                  <span className={d.stale ? "text-rose-600 font-semibold" : "text-emerald-600"}>{d.gps_age_seconds}s</span>
                )}</td>
                <td><Badge status={d.stale ? "offline" : "online"}>{d.stale ? "INATIVO" : "ATIVO"}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.drivers.length === 0 && <div className="p-6 text-center text-zinc-500">Nenhum motorista em entrega Keeta no momento</div>}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
//  TAB: TIMELINE
// ─────────────────────────────────────────────────────────────────────
const LIFECYCLE_ORDER = ["PICKUP_ONGOING", "ARRIVED_AT_MERCHANT", "ORDER_PICKED", "DELIVERY_ONGOING", "ARRIVED_AT_CUSTOMER", "ORDER_DELIVERED"];

const TimelineTab = () => {
  const [orderId, setOrderId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const r = await api.get(url(`/orders/${orderId}/timeline`));
      setData(r.data);
    } catch { toast.error("Pedido não encontrado"); setData(null); }
    finally { setLoading(false); }
  }, [orderId]);

  // Atualização automática quando há um pedido carregado (a cada 15s); não dispara fetch imediato — usuário aciona via botão.
  useSmartPolling(load, 15000, /* immediate */ false);

  const sentMap = useMemo(() => {
    const m = {};
    (data?.events_sent || []).forEach(e => { if (e.success) m[e.event_type] = e.created_at; });
    return m;
  }, [data]);

  return (
    <div className="space-y-3" data-testid="timeline-tab">
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-3 flex gap-2">
        <input value={orderId} onChange={(e) => setOrderId(e.target.value)}
               placeholder="ID do pedido Keeta (ex: ord-abc123...)"
               className="flex-1 border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono"
               data-testid="input-order-id" />
        <button onClick={load} disabled={!orderId || loading}
                className="bg-zinc-900 text-white px-4 py-2 rounded-lg text-sm hover:bg-zinc-700 disabled:opacity-50"
                data-testid="btn-load-timeline">{loading ? "..." : "Buscar"}</button>
      </div>

      {data && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><div className="text-xs text-zinc-500">Status</div><Badge status={data.order.status || "?"} /></div>
            <div><div className="text-xs text-zinc-500">Motorista</div><div className="font-semibold text-sm">{data.driver.name || "—"}</div></div>
            <div><div className="text-xs text-zinc-500">Telefone</div><div className="text-sm">{data.driver.phone || "—"}</div></div>
            <div><div className="text-xs text-zinc-500">Placa</div><div className="font-mono text-sm">{data.driver.plate || "—"}</div></div>
          </div>

          {data.anomalies?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="anomalies">
              <h4 className="font-semibold text-amber-900 mb-2">⚠️ Anomalias Detectadas</h4>
              <div className="space-y-1">
                {data.anomalies.map((a, i) => (
                  <div key={i} className="text-sm flex items-center gap-2"><Badge status={a.level}>{a.level === "CRITICAL" ? "CRÍTICO" : a.level === "WARNING" ? "ATENÇÃO" : a.level}</Badge>
                    <span className="font-mono text-xs">{a.code}</span>
                    <span>{a.msg}</span></div>
                ))}
              </div>
            </div>
          )}

          {/* Visual do ciclo de vida */}
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
            <h4 className="font-semibold mb-3">Ciclo de Vida — Open Delivery v1.5.0</h4>
            <div className="flex flex-wrap gap-2">
              {LIFECYCLE_ORDER.map((evt, i) => (
                <div key={evt} className={`flex-1 min-w-[140px] p-2 rounded-lg border-2 text-center ${sentMap[evt] ? "bg-emerald-50 border-emerald-300" : "bg-zinc-50 border-zinc-200"}`}>
                  <div className="text-[10px] text-zinc-500">#{i+1}</div>
                  <div className="font-mono text-xs font-semibold">{evt}</div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    {sentMap[evt] ? sentMap[evt].replace("T"," ").slice(11,19) : "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
            <h4 className="font-semibold mb-3">Eventos Enviados (saída)</h4>
            <div className="space-y-1">
              {data.events_sent.map((e, i) => (
                <div key={i} className="flex items-center gap-3 border-l-4 border-emerald-500 pl-3 py-1.5" data-testid={`evt-sent-${e.event_type}`}>
                  <span className="font-mono text-[11px] text-zinc-500 w-40">{e.created_at?.replace("T"," ").slice(0,19)}</span>
                  <span className="font-mono text-xs font-semibold flex-1">{e.event_type}</span>
                  {e.simulated && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">[SIMULADO]</span>}
                  <Badge status={e.success ? "SUCCESS" : "DEAD"}>{e.success ? "SUCESSO" : "MORTO"}</Badge>
                </div>
              ))}
              {data.events_sent.length === 0 && <div className="text-zinc-500 text-sm">Nenhum evento enviado ainda</div>}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
            <h4 className="font-semibold mb-3">Webhooks Recebidos (entrada)</h4>
            <div className="space-y-1">
              {data.webhooks_received.map((w, i) => (
                <div key={i} className="flex items-center gap-3 border-l-4 border-sky-500 pl-3 py-1.5" data-testid={`webhook-${i}`}>
                  <span className="font-mono text-[11px] text-zinc-500 w-40">{w.processed_at?.replace("T"," ").slice(0,19)}</span>
                  <span className="font-mono text-xs font-semibold">{w.event_type}</span>
                </div>
              ))}
              {data.webhooks_received.length === 0 && <div className="text-zinc-500 text-sm">Nenhum webhook recebido ainda</div>}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
            <h4 className="font-semibold mb-3">GPS Recente ({data.gps_recent.length})</h4>
            <div className="text-[11px] font-mono space-y-0.5 max-h-48 overflow-y-auto">
              {data.gps_recent.map((g, i) => (
                <div key={i}>{g.ts?.replace("T"," ").slice(0,19)} · {g.lat.toFixed(5)}, {g.lng.toFixed(5)}</div>
              ))}
              {data.gps_recent.length === 0 && <div className="text-zinc-500">Sem dados de GPS</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
//  TAB: SECURITY
// ─────────────────────────────────────────────────────────────────────
const SecurityTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get(url("/security/events")); setData(r.data);
  }, []);
  useSmartPolling(load, 30000);

  if (!data) return <Skeleton />;

  return (
    <div className="space-y-3" data-testid="security-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="Nonces (1h)"  value={data.stats?.nonces_seen_1h ?? 0}  testId="card-nonces-1h" />
        <StatCard title="Nonces (24h)" value={data.stats?.nonces_seen_24h ?? 0} testId="card-nonces-24h" />
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
        <h4 className="font-semibold mb-2">🔐 Camadas de Segurança Ativas</h4>
        <ul className="text-sm space-y-1.5 text-zinc-700">
          <li>✅ Verificação de assinatura HMAC-SHA256 (entrada)</li>
          <li>✅ Anti-replay (janela de timestamp de 5min)</li>
          <li>✅ Anti-replay (unicidade de nonce)</li>
          <li>✅ Idempotência por eventId</li>
          <li>✅ Comparação em tempo constante (anti-timing)</li>
        </ul>
        <div className="mt-3 text-xs text-zinc-500 bg-zinc-50 p-2 rounded font-mono">
          {data.note}
        </div>
        <div className="mt-3 text-xs text-zinc-600">
          <strong>Para SIEM:</strong> use <code className="bg-zinc-100 px-1 rounded">grep [KEETA_SECURITY]</code> em backend.err.log<br/>
          <strong>Eventos rastreados:</strong> KEETA_SIGNATURE_INVALID · KEETA_REPLAY_ATTACK · KEETA_NONCE_DUPLICATE · KEETA_TIMESTAMP_EXPIRED
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
//  TAB: METRICS
// ─────────────────────────────────────────────────────────────────────
const MetricsTab = () => {
  const [m, setM] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get(url("/metrics")); setM(r.data);
  }, []);
  useSmartPolling(load, 30000);

  if (!m) return <Skeleton />;

  const totalEvents = m.events_24h.reduce((s, e) => s + e.total, 0) || 1;

  return (
    <div className="space-y-3" data-testid="metrics-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard title="Enviados (1h)"     value={m.outbox.sent_1h}     subtitle={`${m.outbox.sent_simulated_1h ?? 0} sim · ${m.outbox.sent_live_1h ?? 0} real`} testId="m-sent" />
        <StatCard title="Mortos (1h)"       value={m.outbox.dead_1h}     color={m.outbox.dead_1h > 0 ? "text-rose-600" : ""} testId="m-dead" />
        <StatCard title="Tentativas (1h)"   value={m.outbox.retries_1h}  testId="m-retries" />
        <StatCard title="Latência Média"    value={`${m.outbox.avg_latency_sec}s`} testId="m-latency" />
        <StatCard title="Webhooks (1h)"     value={m.webhooks.processed_1h} testId="m-webhooks" />
        <StatCard title="Motoristas Ativos" value={m.tracking.active_drivers} testId="m-active" />
        <StatCard title="Pings GPS (5m)"    value={m.tracking.gps_pings_5min} testId="m-gps" />
        {m.mode && <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Modo Operacional</div>
          <ModeBadge mode={m.mode} />
        </div>}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-4">
        <h4 className="font-semibold mb-3">Distribuição de Eventos (últimas 24h)</h4>
        {m.events_24h.length === 0 ? (
          <div className="text-zinc-500 text-sm">Nenhum evento nas últimas 24h</div>
        ) : (
          <div className="space-y-2">
            {m.events_24h.map((e) => {
              const pct = (e.total / totalEvents) * 100;
              const okPct = e.total > 0 ? (e.ok / e.total) * 100 : 0;
              return (
                <div key={e.type} data-testid={`bar-${e.type}`}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="font-mono">{e.type}</span>
                    <span className="text-zinc-600">{e.total} <span className="text-emerald-600">✓{e.ok}</span> {e.fail > 0 && <span className="text-rose-600">✗{e.fail}</span>}</span>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded overflow-hidden flex">
                    <div className="bg-emerald-500 h-full" style={{ width: `${pct * okPct / 100}%` }} />
                    <div className="bg-rose-500 h-full" style={{ width: `${pct * (100-okPct) / 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
//  MAIN
// ─────────────────────────────────────────────────────────────────────
export const KeetaOperationsCenter = () => {
  const [tab, setTab] = useState("overview");
  const [mode, setMode] = useState(null);

  // Buscar mode em background para exibir no header sticky
  useEffect(() => {
    let alive = true;
    const fetchMode = async () => {
      try {
        const r = await api.get(url("/health"));
        if (alive) setMode(r.data.mode);
      } catch {}
    };
    fetchMode();
    const t = setInterval(fetchMode, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const tabs = [
    { id: "overview", label: "Visão Geral",    icon: "📊" },
    { id: "outbox",   label: "Fila de Eventos",icon: "📦" },
    { id: "drivers",  label: "Motoristas",     icon: "🛵" },
    { id: "timeline", label: "Linha do Tempo", icon: "🔍" },
    { id: "security", label: "Segurança",      icon: "🔐" },
    { id: "metrics",  label: "Métricas",       icon: "📈" },
  ];

  const TabComp = {
    overview: <OverviewTab />, outbox: <OutboxTab />, drivers: <DriversTab />,
    timeline: <TimelineTab />, security: <SecurityTab />, metrics: <MetricsTab />,
  }[tab];

  return (
    <MainLayout>
      <div className="bg-zinc-50 -m-6 p-0" data-testid="keeta-ops-center">
        <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-zinc-900">Central de Operações Keeta</h1>
                <p className="text-xs text-zinc-500">Monitoramento em tempo real · atualização automática inteligente</p>
              </div>
              <div className="flex items-center gap-3">
                {mode && <ModeBadge mode={mode} />}
                <span className="text-xs text-zinc-500 font-mono">Open Delivery v1.5.0</span>
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex border-b border-zinc-100 -mb-px overflow-x-auto" data-testid="tabs-nav">
              {tabs.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                        className={`px-5 py-2.5 text-sm font-medium whitespace-nowrap transition border-b-2 ${
                          tab === t.id ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"
                        }`}>
                  <span className="mr-1.5">{t.icon}</span>{t.label}
                </button>
              ))}
            </nav>
          </div>
        </header>
        <main className="max-w-7xl mx-auto p-4">{TabComp}</main>
      </div>
    </MainLayout>
  );
};

export default KeetaOperationsCenter;
